import { NotAllowedIcon } from "@chakra-ui/icons";
import { Box, Button, useDisclosure } from "@chakra-ui/react";
import { FC } from "react";

import { RanaModal } from "@nens/ranalib";
import { humanizeBytes } from "@nens/shared-utils";
import { Simulation } from "@nens/threedi/openapi-client";

import { getSimulationsApi, getThreediModelsApi } from "../../apiConfiguration";
import GenerateAndOpenLink from "../../components/GenerateAndOpenLink";
import { useResultFiles } from "../../queries/simulations";
import { useGridadmin } from "../../queries/threedimodels";
import { sum } from "../../utils/functions";

interface DownloadResultButtonProps {
  simulation: Simulation;
}

const DownloadResultButton: FC<DownloadResultButtonProps> = ({ simulation }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const simulationsApi = getSimulationsApi();
  const threediModelsApi = getThreediModelsApi();

  // We add the grid admin file here because it's convenient for users, even though it is not
  // a result file.
  const filesResponse = useResultFiles(simulation.id ?? 0);
  const gridadminResponse = useGridadmin(simulation.threedimodelId);

  if (!filesResponse.isSuccess && !gridadminResponse.isSuccess) return null;

  const results = filesResponse.data ? filesResponse.data.results : [];
  const resultsMinusRemoved = results.filter((result) => !(result.file?.state === "removed"));
  const resultsRemoved = results.filter((result) => result.file?.state === "removed");
  const gridadmin = gridadminResponse.data ?? null;

  const gridadminSize = gridadmin && gridadmin.size ? gridadmin.size : 0;
  const size = sum(resultsMinusRemoved.map((result) => result?.file?.size || 0)) + gridadminSize;

  return (
    <>
      <Button
        // Grid admin files should always be downloadable even though there are no result files for the simulation
        isDisabled={resultsMinusRemoved.length === 0 && !gridadmin}
        variant="secondary"
        onClick={onOpen}
      >
        {resultsMinusRemoved.length + (gridadmin ? 1 : 0)} result files to download (
        {humanizeBytes(size)})
      </Button>
      <RanaModal
        isOpen={isOpen}
        onClose={onClose}
        topBar="Downloadable result files"
        buttons={[
          <Button key="close" variant="primary" onClick={onClose}>
            Close
          </Button>,
        ]}
      >
        {resultsMinusRemoved.map((result) => (
          <Box marginBottom={4}>
            <GenerateAndOpenLink
              filename={result.filename}
              size={result.file?.size || 0}
              fetchUrl={async () => {
                if (!simulation.id || !result.id) return;
                return (
                  await simulationsApi.simulationsResultsFilesDownload({
                    simulationPk: simulation.id,
                    id: result.id,
                  })
                ).getUrl;
              }}
            />
          </Box>
        ))}
        {gridadmin?.getUrl ? (
          <Box marginBottom={4}>
            <GenerateAndOpenLink
              filename={"gridadmin.h5"}
              size={gridadminSize || 0}
              fetchUrl={async () => {
                return (
                  await threediModelsApi.threedimodelsGridadminDownload({
                    id: simulation.threedimodelId ?? 0,
                  })
                ).getUrl;
              }}
            />
          </Box>
        ) : null}
        {resultsRemoved.map((result) => (
          <Box marginBottom={4} title="Not possible to download removed files">
            <NotAllowedIcon boxSize="32px" color="brand.accent" />
            {result.filename} (Removed)
          </Box>
        ))}
      </RanaModal>
    </>
  );
};

export default DownloadResultButton;
